import {
  TeliaBannerBillboard,
  TeliaCol,
  TeliaCtaLink,
  TeliaGrid,
  TeliaHeroBanner,
  TeliaRow,
  TeliaTextSpacing,
  TeliaVideoPlayer,
} from '@teliads/components/react';
import { ReactNode, useCallback, useLayoutEffect, useState } from 'react';

import Heading from '../heading/Heading';
import Paragraph from '../paragraph/Paragraph';

import { useOnWindowResize } from 'utils/hooks/useOnWindowResize';
import { CtaContent } from 'utils/models';
import './HeroBanner.scss';
import { getLinkTarget } from 'utils/utils';

interface Video {
  url: string | undefined;
  poster: string | undefined;
  altText?: string;
}

interface Props {
  title: string;
  headingVariant?: 'display-200' | 'display-100' | 'display-25';
  preamble?: string | ReactNode;
  video?: Video;
  cta?: CtaContent;
  billboardEnabled?: boolean;
}

export default function HeroBanner({
  title,
  headingVariant = 'display-200',
  preamble,
  video,
  cta,
  billboardEnabled,
}: Props) {
  const [headingSize, setHeadingSize] = useState(headingVariant);

  const handleResize = useCallback(() => {
    if (headingVariant != 'display-25') {
      if (window.innerWidth <= 365) {
        setHeadingSize('display-100');
      } else {
        setHeadingSize('display-200');
      }
    }
  }, [headingVariant]);

  useOnWindowResize(handleResize);

  useLayoutEffect(() => {
    handleResize();
  }, [handleResize]);

  function renderBannerBody() {
    return (
      <TeliaTextSpacing>
        <Heading tag="h1" variant={headingSize}>
          {title
            .split('   ')
            .filter((line) => line.trim() !== '')
            .map((line) => (
              <span className="hero-banner__title-item" key={line}>
                {line.trim()}
              </span>
            ))}
        </Heading>

        {preamble && (
          <Paragraph
            className="richtext-paragraph"
            variant={!billboardEnabled ? 'preamble-200' : 'preamble-100'}
          >
            {preamble}
          </Paragraph>
        )}

        {cta && (
          <TeliaCtaLink
            href={cta.url}
            target={getLinkTarget(cta.url)}
            size="md"
            variant="expressive"
            dataTestid="hero-banner-cta-link"
          >
            {cta.text}
          </TeliaCtaLink>
        )}
      </TeliaTextSpacing>
    );
  }

  const wrapperVideoClass = billboardEnabled
    ? 'hero-banner-wrapper--with-video-billboard'
    : 'hero-banner-wrapper--with-video';

  return (
    <article
      className={`hero-banner-wrapper ${video ? wrapperVideoClass : ''}`}
      data-testid="hero-banner-wrapper"
      key={title + video?.url}
    >
      {!video ? (
        <TeliaGrid>
          <TeliaRow>
            <TeliaCol width={12}>{renderBannerBody()}</TeliaCol>
          </TeliaRow>
        </TeliaGrid>
      ) : billboardEnabled ? (
        <TeliaBannerBillboard
          dark
          enableVideo
          className="hero-banner"
          imgSrc={''}
          imgAlt={video.altText}
        >
          {renderBannerBody()}
          <TeliaVideoPlayer
            autoplay
            loop
            muted
            slot="video"
            type="html"
            src={video.url}
            poster={video.poster}
            className="hero-banner__video-player"
            dataTestid="hero-banner-video-player"
          />
        </TeliaBannerBillboard>
      ) : (
        <TeliaHeroBanner
          imgSrc=""
          backgroundImageFrom="lg"
          enableVideo
          className="hero-banner"
          imgAlt={video.altText}
        >
          {renderBannerBody()}
          <TeliaVideoPlayer
            autoplay
            loop
            muted
            slot="video"
            type="html"
            src={video.url}
            poster={video.poster}
            className="hero-banner__video-player"
            dataTestid="hero-banner-video-player"
          />
        </TeliaHeroBanner>
      )}
    </article>
  );
}
